import React, { useEffect, useMemo, useRef, useState } from "react";
import "./testing_tool_results.css";
import TestingToolIssueAccordion from "./testing_tool_issue_accordion";
import { exportTestingToolReport } from "./testing_tool_api_service";

const SEVERITY_OPTIONS = ["All", "Low", "Medium", "High"];
const SEGMENT_OPTIONS = ["All", "High", "Medium", "Low"];

function getEffortLevel(issue) {
  if (!issue) return "";
  if (typeof issue.effort === "string") return issue.effort;
  if (issue.effort && typeof issue.effort === "object") {
    return issue.effort.level || "";
  }
  if (typeof issue.effort_level === "string") return issue.effort_level;
  return "";
}

function normalizeSeverity(value) {
  const severity = String(value || "").toLowerCase();
  if (severity === "high" || severity === "critical") return "High";
  if (severity === "medium" || severity === "moderate") return "Medium";
  return "Low";
}

function normalizeIssue(issue, index) {
  const category =
    issue.category ||
    (Array.isArray(issue.page_type) && issue.page_type[0]) ||
    issue.domain ||
    "General";

  return {
    ...issue,
    id: issue.id || issue.issue_id || `issue-${index}`,
    issue_id: issue.issue_id || issue.id || `issue-${index}`,
    category,
    title: issue.title || issue.issue_title || "Untitled issue",
    issue: issue.issue || issue.user_problem || "",
    potentialImpact: issue.potentialImpact || issue.dot_criterion || "",
    suggestedSolution: issue.suggestedSolution || issue.recommendation || "",
    severity: normalizeSeverity(issue.severity),
    effort: issue.effort || issue.effort_level || "Low",
    resolved: Boolean(issue.resolved),
    signals_to_detect: Array.isArray(issue.signals_to_detect)
      ? issue.signals_to_detect
      : [],
  };
}

export default function TestingToolResults(testingToolResultsProps) {
  const {
    siteType,
    message,
    retrievedIssues,
    totalOpenFindings,
    totalResolvedFindings,
    totalEstimatedFixHours,
    testTestingToolId,
    onReaudit,
    onBackToProjects,
    isReauditing,
  } = testingToolResultsProps;

  const toolbarRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState("All categories");
  const [selectedSeverity, setSelectedSeverity] = useState("All");
  const [selectedSegment, setSelectedSegment] = useState("All");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSeverityOpen, setIsSeverityOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [issues, setIssues] = useState(
    Array.isArray(retrievedIssues) ? retrievedIssues : [],
  );

  useEffect(() => {
    setIssues(Array.isArray(retrievedIssues) ? retrievedIssues : []);
  }, [retrievedIssues]);

  const normalizedIssues = useMemo(
    () => issues.map((issue, index) => normalizeIssue(issue, index)),
    [issues],
  );

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(normalizedIssues.map((item) => item.category)),
    );
    return ["All categories", ...unique];
  }, [normalizedIssues]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!toolbarRef.current?.contains(event.target)) {
        setIsCategoryOpen(false);
        setIsSeverityOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isReviewModalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [isReviewModalOpen]);

  const filteredIssues = normalizedIssues.filter((item) => {
    const byCategory =
      selectedCategory === "All categories" ||
      item.category === selectedCategory;

    const bySearch =
      !searchTerm.trim() ||
      [
        item.title,
        item.issue,
        item.potentialImpact,
        item.suggestedSolution,
        item.category,
        ...(item.signals_to_detect || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());

    const bySegment =
      selectedSegment === "All" ||
      getEffortLevel(item).toLowerCase() === selectedSegment.toLowerCase();

    const byDropdownSeverity =
      selectedSeverity === "All" ||
      item.severity.toLowerCase() === selectedSeverity.toLowerCase();

    return byCategory && bySearch && bySegment && byDropdownSeverity;
  });

  const issuesByCategory = filteredIssues.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});
  const hasIssuesByCategory = Object.keys(issuesByCategory).length > 0;

  const totalIssuesCount = normalizedIssues.length;
  const resolvedIssuesCount = normalizedIssues.filter(
    (item) => item.resolved,
  ).length;
  const resolvedRatio =
    totalIssuesCount > 0 ? (resolvedIssuesCount / totalIssuesCount) * 100 : 0;
  const safeTotalFindings =
    typeof totalOpenFindings === "number" && totalOpenFindings > 0
      ? totalOpenFindings
      : totalIssuesCount;
  const safeResolvedFindings =
    typeof totalResolvedFindings === "number"
      ? totalResolvedFindings
      : resolvedIssuesCount;
  const summaryResolvedPercent =
    safeTotalFindings > 0
      ? (safeResolvedFindings / safeTotalFindings) * 100
      : 0;
  const severityCounts = useMemo(
    () =>
      normalizedIssues.reduce(
        (acc, issue) => {
          const key = issue.severity.toLowerCase();
          if (key === "high" || key === "medium" || key === "low") {
            acc[key] += 1;
          }
          return acc;
        },
        { high: 0, medium: 0, low: 0 },
      ),
    [normalizedIssues],
  );
  const majorCategorySummary = useMemo(() => {
    const rankedCategories = Object.entries(
      normalizedIssues.reduce((acc, issue) => {
        acc[issue.category] = (acc[issue.category] || 0) + 1;
        return acc;
      }, {}),
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([category]) => category);

    if (rankedCategories.length === 0) return "No dominant category yet.";
    if (rankedCategories.length === 1) return rankedCategories[0];
    return `${rankedCategories[0]} and ${rankedCategories[1]}`;
  }, [normalizedIssues]);
  const priorityActionText =
    severityCounts.high > 0
      ? "Start with high-severity blockers, then tackle medium-impact flow fixes, and finish with low-severity polish."
      : severityCounts.medium > 0
      ? "Prioritize medium-severity flow and clarity issues first, then close low-severity improvements."
      : "Focus on low-severity refinements to improve overall usability and consistency.";

  // --- Score Card ---
  // const actionPlanScore = 74;
  // const scoreRadius = 64;
  // const scoreCircumference = 2 * Math.PI * scoreRadius;
  // const scoreProgress = Math.min(Math.max(actionPlanScore, 0), 100) / 100;
  // const scoreDashoffset = scoreCircumference * (1 - scoreProgress);
  // --- Score Card End ---

  function handleToggleResolved(issueId) {
    setIssues((prev) =>
      prev.map((item) =>
        item.id === issueId || item.issue_id === issueId
          ? { ...item, resolved: !item.resolved }
          : item,
      ),
    );
  }

  async function handleExport() {
    if (!testTestingToolId || isExporting || isReauditing) return;

    try {
      setIsExporting(true);
      const data = await exportTestingToolReport(testTestingToolId);
      const fileUrl = data?.file_url || data?.url;
      if (!fileUrl) {
        throw new Error("Export file URL is missing");
      }
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to export testing tool report", error);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="">
      <div className="testing-tool-results-top">
        <div>
          <p className="testing-tool-results-breadcrumb">
            <button
              type="button"
              className="testing-tool-results-breadcrumb-link"
              onClick={onBackToProjects}
            >
              Projects
            </button>
            <img src="/img/testing-tool/Vector.svg" alt="nagivation arrow" />
            <span>{siteType} website</span>
          </p>
          <h2 className="testing-tool-results-title">
            {siteType} website results
          </h2>
          <p className="testing-tool-results-subtitle">{message}</p>
        </div>
        <div className="testing-tool-results-actions">
          <button
            type="button"
            className="testing-tool-results-btn"
            onClick={handleExport}
            disabled={isReauditing || isExporting || !testTestingToolId}
          >
            <img src="/img/testing-tool/export.svg" alt="export icon" />
            {isExporting ? "Exporting..." : "Export"}
          </button>
          <button
            type="button"
            className={`testing-tool-results-btn testing-tool-results-btn--dark${isReauditing ? " is-loading" : ""
              }`}
            onClick={onReaudit}
            disabled={isReauditing}
          >
            {isReauditing ? (
              <img
                src="/img/testing-tool/SpinnerGap.svg"
                alt=""
                aria-hidden
                className="testing-tool-results-btn-spinner"
              />
            ) : (
              <img src="/img/testing-tool/Re-audit.svg" alt="export icon" />
            )}
            {isReauditing ? "Re-auditing..." : "Re-audit"}
          </button>
        </div>
      </div>

      <div
        className={`testing-tool-results-cards ${isReauditing ? "is-disabled" : ""
          }`}
        aria-disabled={isReauditing}
      >
        <article className="testing-tool-results-card">
          <div className="testing-tool-results-card-head">
            <span className="testing-tool-results-icon">
              <img
                src="/img/testing-tool/AuditSummary.svg"
                alt="Audit Summary"
              />
            </span>
            <p className="testing-tool-results-card-title">Audit summary</p>
          </div>
          <p className="testing-tool-results-card-value">
            {safeResolvedFindings}
            <span>/{safeTotalFindings}</span>
          </p>
          <div className="testing-tool-results-card-row">
            <p>Open findings</p>
            <span>{Math.round(summaryResolvedPercent)}% resolved</span>
          </div>
          <div className="testing-tool-results-progress">
            <span
              style={{
                width: `${Math.min(Math.max(summaryResolvedPercent, 0), 100)}%`,
              }}
            />
          </div>
        </article>

        <article className="testing-tool-results-card">
          <div className="testing-tool-results-card-head">
            <span className="testing-tool-results-icon">
              <img
                src="/img/testing-tool/Criticalissues.svg"
                alt="Critical issues"
              />
            </span>
            <p className="testing-tool-results-card-title">Critical issues</p>
          </div>
          <p className="testing-tool-results-card-value">
            {retrievedIssues.length}
          </p>
          <p className="testing-tool-results-card-row">
            High severity needs attention
          </p>
        </article>

        <article className="testing-tool-results-card">
          <div className="testing-tool-results-card-head">
            <span className="testing-tool-results-icon">
              <img src="/img/testing-tool/Esteffort.svg" alt="Est. effort" />
            </span>
            <p className="testing-tool-results-card-title">Est. effort</p>
          </div>
          <p className="testing-tool-results-card-value">
            {totalEstimatedFixHours}
          </p>
          <p className="testing-tool-results-card-row">
            Total implementation time
          </p>
        </article>
      </div>

      <div
        className={`testing-tool-results-filter ${isReauditing ? "is-disabled" : ""
          }`}
        ref={toolbarRef}
        aria-disabled={isReauditing}
      >
        <div className="testing-tool-results-search-wrap">
          <img
            src="/img/testing-tool/search.svg"
            alt=""
            aria-hidden
            className="testing-tool-results-search-icon"
          />
          <input
            type="text"
            className="testing-tool-results-search"
            placeholder="Search issues, categories, or keywords..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="testing-tool-results_wrapper">
          <div className="testing-tool-results-severity">
            {SEGMENT_OPTIONS.map((segment) => (
              <button
                key={segment}
                type="button"
                className={selectedSegment === segment ? "is-active" : ""}
                onClick={() => setSelectedSegment(segment)}
              >
                {segment}
              </button>
            ))}
          </div>
          <div className="testing-tool-results-divider" />
          <div className="testing-tool-results-dropdown-wrap">
            <button
              type="button"
              className={`testing-tool-results-category${isCategoryOpen ? " is-open" : ""
                }`}
              onClick={() => {
                setIsCategoryOpen((open) => !open);
                setIsSeverityOpen(false);
              }}
            >
              {selectedCategory}
              <img
                src="/img/testing-tool/arrow-down-round.svg"
                alt=""
                aria-hidden
                className="testing-tool-results-arrow"
              />
            </button>
            {isCategoryOpen ? (
              <div className="testing-tool-results-menu testing-tool-results-menu--category">
                {categories.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`testing-tool-results-menu-item${selectedCategory === option ? " is-selected" : ""
                      }`}
                    onClick={() => {
                      setSelectedCategory(option);
                      setIsCategoryOpen(false);
                    }}
                  >
                    <span>{option}</span>
                    {selectedCategory === option ? (
                      <span className="testing-tool-results-check" aria-hidden>
                        <img
                          src="/img/testing-tool/filterTick.svg"
                          alt="filterTick"
                        />
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="testing-tool-results-dropdown-wrap">
            <button
              type="button"
              className={`testing-tool-results-view${isSeverityOpen ? " is-open" : ""
                }`}
              onClick={() => {
                setIsSeverityOpen((open) => !open);
                setIsCategoryOpen(false);
              }}
            >
              <img src="/img/testing-tool/sorting.svg" alt="Shorting" />
            </button>
            {isSeverityOpen ? (
              <div className="testing-tool-results-menu testing-tool-results-menu--severity">
                {SEVERITY_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`testing-tool-results-menu-item${selectedSeverity === option ? " is-selected" : ""
                      }`}
                    onClick={() => {
                      setSelectedSeverity(option);
                      setIsSeverityOpen(false);
                    }}
                  >
                    <span>{option}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* --- Score Card ---  */}
      {/* <section className="testing-tool-results-score-card">
        <div className="testing-tool-results-score-ring">
          <svg
            className="testing-tool-results-score-svg"
            viewBox="0 0 160 160"
            aria-hidden
          >
            <circle
              className="testing-tool-results-score-track"
              cx="80"
              cy="80"
              r={scoreRadius}
            />
            <circle
              className="testing-tool-results-score-progress"
              cx="80"
              cy="80"
              r={scoreRadius}
              strokeDasharray={scoreCircumference}
              strokeDashoffset={scoreDashoffset}
            />
          </svg>
          <div className="testing-tool-results-score-inner">
            <p className="testing-tool-results-score-value">{actionPlanScore}</p>
            <p className="testing-tool-results-score-label">Score</p>
          </div>
        </div>
        <button type="button" className="testing-tool-results-score-btn">
          Go to action plan
        </button>
      </section> */}
      {/* --- Score Card ---  */}
      <div
        className={`testing-tool-results-findings ${isReauditing ? "is-disabled" : ""
          }`}
        aria-disabled={isReauditing}
      >
        <div className="testing-tool-results-issues-summary">
          <p className="testing-tool-results-issues-summary-title">
            Issues overview
          </p>
          <p className="testing-tool-results-issues-summary-copy">
            Major issues are concentrated in {majorCategorySummary}, with{" "}
            {severityCounts.high} high, {severityCounts.medium} medium, and{" "}
            {severityCounts.low} low severity findings. Overall UX shows{" "}
            {severityCounts.high > 0
              ? "critical friction in key journeys"
              : severityCounts.medium > 0
              ? "moderate friction across important flows"
              : "minor friction that mostly affects polish"}
            . {priorityActionText}
          </p>
        </div>
        {hasIssuesByCategory ? (
          Object.entries(issuesByCategory).map(([category, categoryIssues]) => (
            <TestingToolIssueAccordion
              key={category}
              sectionLabel={category}
              items={categoryIssues}
              onToggleResolved={handleToggleResolved}
            />
          ))
        ) : (
          <p className="testing-tool-results-empty-message">
            No issues found for the selected filters.
          </p>
        )}
      </div>

      <div
        className="testing-tool-results-fixed-strip"
        role="region"
        aria-label="Review progress"
      >
        <div className="testing-tool-results-fixed-progress">
          <img src="img/testing-tool/issuesresolved.svg" alt="issuesresolved" />
          <p>
            {resolvedIssuesCount} of {totalIssuesCount} issues resolved
          </p>
          <div className="testing-tool-results-fixed-progressbar" aria-hidden>
            <span style={{ width: `${resolvedRatio}%` }} />
          </div>
        </div>
        <button
          type="button"
          className="testing-tool-results-fixed-cta"
          onClick={() => setIsReviewModalOpen(true)}
          disabled={isReauditing}
        >
          Complete review
        </button>
      </div>

      {isReviewModalOpen ? (
        <div
          className="testing-tool-results-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="testing-tool-results-modal">
            <div className="testing-tool-results-modal-icon" aria-hidden>
              <img src="/img/testing-tool/reviewIcon.svg" alt="reviewIcon" />
            </div>
            <h3>Review completed!</h3>
            <p>
              You&apos;ve resolved {resolvedIssuesCount} out of{" "}
              {totalIssuesCount} findings. Great progress on improving the user
              experience!
            </p>
            <div className="testing-tool-results-modal-stats">
              <div className="testing-tool-results-modal-stat-card">
                <strong>{resolvedIssuesCount}</strong>
                <span>FIXED</span>
              </div>
              <div className="testing-tool-results-modal-stat-card">
                <strong>
                  {Math.max(totalIssuesCount - resolvedIssuesCount, 0)}
                </strong>
                <span>REMAINING</span>
              </div>
            </div>
            <div className="testing-tool-results-modal-actions">
              <button
                type="button"
                className="testing-tool-results-modal-btn testing-tool-results-modal-btn--dark"
                onClick={() => {
                  window.location.href = "/testing-tool";
                }}
              >
                Back to Projects
              </button>
              <button
                type="button"
                className="testing-tool-results-modal-btn"
                onClick={() => setIsReviewModalOpen(false)}
              >
                Continue reviewing
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
